import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  ActionIcon,
  Badge,
  Button,
  Modal,
  Paper,
  Progress,
  Select,
  Text,
  Textarea,
} from "@mantine/core";
import { IconHistory, IconTrash } from "@tabler/icons-react";
import { showNotification } from "@mantine/notifications";
import {
  formatDatetoISO,
  getActFrequencyText,
  getComplianceBadgeColor,
  getDays,
  getRecFrequencyText,
  getStatusColor,
  getStatusText,
} from "./utilities";
import BusinessUnitName from "../componenets/getBuName";
import ObligationTranHistoryModal from "./ObligationTranHistoryModal";
import ActionHistory from "../componenets/actionHistory";
import EvidenceVerificationResult, {
  verificationStatusColor,
} from "../componenets/EvidenceVerificationResult";
import "./common.css";
import {
  attachFiles,
  attachFileToParent,
  deleteFile,
  getReadUrl,
} from "../apis/fileApi";
import {
  getObligationDetails,
  getObligationFiles,
  updateObligationApi,
  verifyEvidence,
  getVerifyEvidenceStatus,
  getVerifyEvidenceResult,
  assignObligationApi,
} from "../apis/obligationApi";
import { getUsersByDepartment } from "../apis/userApis";
import axios from "axios";
import { getCookiesSession } from "../cookiesSession";

const ViewObligation = () => {
  const { id } = useParams();
  const [configs, setConfigs] = useState({
    loading: true,
    obligationData: null,
  });
  const [ahModal, setAhModal] = useState(false);
  const [ahId, setAhId] = useState(0);
  const [buttonConfigs, setButtonConfigs] = useState({ refresh: false });
  const cookieData = JSON.parse(getCookiesSession("LawSignaUserDataMaster"));
  const userData = cookieData.user;
  const [files, setFiles] = useState([]);
  // Files already covered by an earlier save, so a new save only records
  // evidence uploaded since then, not the whole historical file list.
  const [baselineFileIds, setBaselineFileIds] = useState(new Set());
  // Compliance choice for THIS save - starts empty every time so a Save can
  // never silently reuse the obligation's existing status without the user
  // consciously picking one.
  const [selectedCompliance, setSelectedCompliance] = useState("");
  const [complianceError, setComplianceError] = useState("");
  const [verification, setVerification] = useState({});
  const [resultModal, setResultModal] = useState({ opened: false, fileId: null });
  const [assignableUsers, setAssignableUsers] = useState([]);
  // { fileName, percent } while an evidence file is uploading, else null.
  const [uploadProgress, setUploadProgress] = useState(null);

  useEffect(() => {
    if (!configs.obligationData?.department) return;
    const loadAssignableUsers = async () => {
      const response = await getUsersByDepartment({
        department: configs.obligationData.department,
      });
      if (response.success) setAssignableUsers(response.users);
    };
    loadAssignableUsers();
  }, [configs.obligationData?.department]);

  const loadObligation = async () => {
    const response = await getObligationDetails({ id: id });
    setConfigs({
      loading: false,
      obligationData: {
        ...response.obligation,
        updatedBy: userData.name,
        remarks: "",
      },
    });
  };

  useEffect(() => {
    loadObligation();
    const getfiles = async () => {
      const response = await getObligationFiles({ id: id });
      if (response.success) {
        setFiles(response.attachedFiles);
        setBaselineFileIds(new Set(response.attachedFiles.map((f) => f.id)));
        response.attachedFiles.forEach(async (file) => {
          try {
            const resultResp = await getVerifyEvidenceResult({ fileId: file.id });
            if (resultResp.success && resultResp.verification) {
              setVerification((prev) => ({
                ...prev,
                [file.id]: { loading: false, result: resultResp.verification.result },
              }));
            }
          } catch (error) {
            // no stored result yet - not an error the user needs to see
          }
        });
      }
    };
    getfiles();
  }, [id]);

  const uploadFileToAws = async (e) => {
    const file = e.target.files[0];
    const fileName = file.name;
    const type = file.type;

    const response = await attachFileToParent({
      extension: "pdf",
      contentType: file.type,
    });
    setUploadProgress({ fileName, percent: 0 });
    const reader = new FileReader();
    reader.onload = async () => {
      const fileData = reader.result;
      const awsAxios = axios.create({
        transformRequest: (data, headers) => {
          headers["Content-Type"] = file.type;
          return data;
        },
      });

      await awsAxios({
        url: response.signedUrl,
        method: "put",
        data: fileData,
        onUploadProgress: (progressEvent) => {
          const percent = progressEvent.total
            ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
            : 0;
          setUploadProgress({ fileName, percent });
        },
      }).then(async () => {
        const fileforAttach = {
          createdBy: userData.name,
          createdByUserId: userData.id,
          location: response.destination,
          name: file.name,
          parent: "obligation",
          parentId: parseInt(id),
          type: type,
        };
        const fileAttached = await attachFiles({ body: fileforAttach });
        setFiles([...files, fileAttached.response]);
        setUploadProgress(null);
      }).catch(() => {
        setUploadProgress(null);
        showNotification({
          color: "red",
          title: "Upload failed",
          message: "Unable to upload the file. Please try again.",
        });
      });
    };
    reader.readAsArrayBuffer(file);
  };

  const openFile = async ({ file }) => {
    const hostedLink = file.hostedLink;
    const response = await getReadUrl({ hostedLink });
    if (response.success) {
      window.open(`${response.signedUrl}`, "_blank");
    }
  };
  const deleteFileFromAws = async ({ e, fileId }) => {
    e.preventDefault();
    const response = await deleteFile({ id: fileId });
    if (response.success) {
      const updatedArray = files.filter((item) => item.id !== fileId);
      setFiles(updatedArray);
    }
  };

  const pollVerificationStatus = async (fileId, taskId, deadline) => {
    let statusResp;
    try {
      statusResp = await getVerifyEvidenceStatus({ taskId, fileId, obligationId: id });
    } catch (error) {
      setVerification((prev) => ({
        ...prev,
        [fileId]: { loading: false, error: "Unable to fetch verification status." },
      }));
      return;
    }
    if (statusResp.state === "SUCCESS") {
      const result = statusResp.result && statusResp.result.result;
      setVerification((prev) => ({ ...prev, [fileId]: { loading: false, result } }));
      setResultModal({ opened: true, fileId });
      return;
    }
    if (statusResp.state === "FAILURE") {
      setVerification((prev) => ({
        ...prev,
        [fileId]: { loading: false, error: "Verification failed. Please try again." },
      }));
      return;
    }
    if (Date.now() > deadline) {
      setVerification((prev) => ({
        ...prev,
        [fileId]: { loading: false, error: "Verification is taking longer than expected." },
      }));
      return;
    }
    setTimeout(() => pollVerificationStatus(fileId, taskId, deadline), 3000);
  };

  const startEvidenceVerification = async (file) => {
    setVerification((prev) => ({ ...prev, [file.id]: { loading: true } }));
    try {
      const response = await verifyEvidence({ obligationId: parseInt(id), fileId: file.id });
      if (response.success) {
        pollVerificationStatus(file.id, response.task_id, Date.now() + 90 * 1000);
      } else {
        setVerification((prev) => ({
          ...prev,
          [file.id]: { loading: false, error: response.error || "Unable to start verification." },
        }));
      }
    } catch (error) {
      setVerification((prev) => ({
        ...prev,
        [file.id]: { loading: false, error: "Unable to start verification." },
      }));
    }
  };

  const updateObligation = async (e) => {
    e.preventDefault();
    if (!selectedCompliance) {
      setComplianceError("Please select a compliance status");
      showNotification({
        color: "red",
        title: "Compliance required",
        message: "Please select a compliance status before saving.",
      });
      return;
    }
    // Only evidence uploaded since the last save belongs to this compliance
    // action - not every file ever attached to the obligation. A rejected
    // AI verdict on any of these files overrides the chosen status server-side.
    const fileIds = files
      .filter((f) => !baselineFileIds.has(f.id))
      .map((f) => f.id)
      .join(",");
    const updatedData = {
      ...configs.obligationData,
      compliant: selectedCompliance,
      updatedBy: userData.name,
      donebyId: userData.id,
      donebyEmail: userData.email,
      remarks: configs.obligationData.remarks,
      fileIds,
    };
    const response = await updateObligationApi({ body: updatedData });
    if (response.success) {
      setBaselineFileIds(new Set(files.map((f) => f.id)));
      setSelectedCompliance("");
      showNotification({
        color: "green",
        title: "obligation update",
        message: "obligation updated",
      });
      await loadObligation();
      setButtonConfigs((prev) => ({ ...prev, refresh: !prev.refresh }));
    } else {
      showNotification({
        color: "red",
        title: "obligation update",
        message: "obligation not updated",
      });
    }
  };
  const updateObligationnmi = async (extraCol3Value) => {
    let updatedData = {
      ...configs.obligationData,
      extraCol3: extraCol3Value,
      compliant: "partially-compliant",
      updatedBy: userData.name,
      donebyId: userData.id,
      donebyEmail: userData.email,
      remarks: remarks,
    };
    if (extraCol3Value === "2") {
      updatedData = {
        ...configs.obligationData,
        extraCol3: extraCol3Value,
        updatedBy: userData.name,
        donebyId: userData.id,
        donebyEmail: userData.email,
        remarks: remarks,
      };
    }
    if (extraCol3Value === "3") {
      updatedData = {
        ...configs.obligationData,
        extraCol3: extraCol3Value,
        compliant: "compliant",
        updatedBy: userData.name,
        remarks: remarks,
        donebyId: userData.id,
        donebyEmail: userData.email,
      };
    }

    const response = await updateObligationApi({ body: updatedData });
    if (response.success) {
      showNotification({
        color: "green",
        title: "obligation update",
        message: "obligation updated",
      });
      setShowModal(false);
      await loadObligation();
      setButtonConfigs((prev) => ({ ...prev, refresh: !prev.refresh }));
    } else {
      showNotification({
        color: "red",
        title: "obligation update",
        message: "obligation not updated",
      });
    }
  };

  const saveAssignee = async (assignToId) => {
    setConfigs({
      ...configs,
      obligationData: {
        ...configs.obligationData,
        assignTo: assignableUsers.find((u) => u.id === parseInt(assignToId)) || null,
      },
    });
    const response = await assignObligationApi({ id, assignToId });
    if (!response.success) {
      showNotification({
        color: "red",
        title: "Assignment failed",
        message: "Unable to update the assignee. Please try again.",
      });
    }
  };

  const [showModal, setShowModal] = useState(false);
  const [remarks, setRemarks] = useState("");
  // Whoever this obligation is assigned to can act on it (upload evidence,
  // set compliance, save) even without the DeptAdmin/DeptUser org role -
  // assignment is what makes an obligation someone's responsibility now,
  // same as the WhatsApp bot's own authorization check.
  const isAssignee = configs.obligationData?.assignTo?.id === userData.id;
  const canAct =
    userData.loginAs.name === "DeptAdmin" ||
    userData.loginAs.name === "DeptUser" ||
    isAssignee;
  const canReassign = ["DeptAdmin", "DeptUser", "Admin", "Editor"].includes(
    userData.loginAs.name
  );

  return (
    <>
      {!configs.loading && configs.obligationData && (
        <>
          <Modal
            opened={ahModal}
            onClose={() => {
              setAhModal(false);
              setAhId(0);
            }}
            title="oblligation action history"
            size="calc(80vw)"
          >
            <ObligationTranHistoryModal id={ahId} />
          </Modal>
          <Modal
            opened={resultModal.opened}
            onClose={() => setResultModal({ opened: false, fileId: null })}
            title="Evidence Verification"
            size="calc(60vw)"
          >
            {resultModal.fileId && verification[resultModal.fileId]?.result && (
              <EvidenceVerificationResult result={verification[resultModal.fileId].result} />
            )}
          </Modal>

          <div
            className="flex justify-between items-start mx-4 mt-2 pb-4"
            style={{ borderBottom: "1px solid #e5e9eb" }}
          >
            <div>
              <Text size="xl" weight={700} style={{ color: "#191D23" }}>
                Obligation-{id}
              </Text>
              <Text size="sm" className="mt-1">
                <span className="blue font-medium">Created On: </span>
                {formatDatetoISO(configs.obligationData.startDate)}
              </Text>
            </div>
            <div className="flex flex-col items-end">
              <div className="flex items-center">
                {configs.obligationData.compliant !== "compliant" && (
                  <Badge
                    variant="light"
                    color={getStatusColor(configs.obligationData.status)}
                  >
                    {getStatusText(configs.obligationData.status)}
                  </Badge>
                )}
                <ActionIcon
                  className="ml-4"
                  onClick={() => {
                    setAhId(id);
                    setAhModal(true);
                  }}
                  color="white"
                >
                  <IconHistory size={20} />
                </ActionIcon>
              </div>
              {configs.obligationData.compliant !== "compliant" && (
                <Text size="sm" className="mt-1" color="dimmed">
                  Pending since {getDays(configs.obligationData.startDate)} day(s)
                </Text>
              )}
            </div>
          </div>

          <Paper withBorder radius="md" p="md" className="mx-4 mt-6">
            <Text className="font-semibold mb-4" style={{ color: "#1b6f8d", letterSpacing: "0.03em" }}>
              CONTRACT DETAILS
            </Text>
            <div className="grid grid-cols-4 gap-6 text-left">
              <div>
                <Text size="xs" className="blue uppercase" style={{ letterSpacing: "0.04em" }}>
                  Department
                </Text>
                <Text className="font-medium mt-1">{configs.obligationData.dept?.name}</Text>
              </div>
              <div>
                <Text size="xs" className="blue uppercase" style={{ letterSpacing: "0.04em" }}>
                  AIN
                </Text>
                <Text className="font-medium mt-1">
                  {configs.obligationData.local && "local-"}
                  {configs.obligationData.connectedAgreement}
                </Text>
              </div>
              <div>
                <Text size="xs" className="blue uppercase" style={{ letterSpacing: "0.04em" }}>
                  Contract Category
                </Text>
                <Text className="font-medium mt-1">{configs.obligationData.extraCol1}</Text>
              </div>
              <div>
                <Text size="xs" className="blue uppercase" style={{ letterSpacing: "0.04em" }}>
                  Business Unit
                </Text>
                <Text className="font-medium mt-1">
                  <BusinessUnitName businessUnitId={configs.obligationData.businessUnit} />
                </Text>
              </div>
              <div>
                <Text size="xs" className="blue uppercase" style={{ letterSpacing: "0.04em" }}>
                  Connected Party
                </Text>
                <Text className="font-medium mt-1">{configs.obligationData.connectedParty}</Text>
              </div>
            </div>
          </Paper>

          <Paper withBorder radius="md" p="md" className="mx-4 mt-4">
            <Text className="font-semibold mb-4" style={{ color: "#1b6f8d", letterSpacing: "0.03em" }}>
              OBLIGATION DETAILS
            </Text>
            <div className="grid grid-cols-4 gap-6 text-left">
              <div>
                <Text size="xs" className="blue uppercase" style={{ letterSpacing: "0.04em" }}>
                  Clause Number
                </Text>
                <Text className="font-medium mt-1">{configs.obligationData.clNo}</Text>
              </div>
              <div>
                <Text size="xs" className="blue uppercase" style={{ letterSpacing: "0.04em" }}>
                  Title
                </Text>
                <Text className="font-medium mt-1">{configs.obligationData.title}</Text>
              </div>
              <div>
                <Text size="xs" className="blue uppercase" style={{ letterSpacing: "0.04em" }}>
                  Obligation
                </Text>
                <Text className="font-medium mt-1">{configs.obligationData.desc}</Text>
              </div>
              <div>
                <Text size="xs" className="blue uppercase" style={{ letterSpacing: "0.04em" }}>
                  Assigned To
                </Text>
                <Text className="font-medium mt-1">
                  {configs.obligationData.assignTo?.name || "Unassigned"}
                </Text>
              </div>
              {configs.obligationData.actFrequency !== 1 && (
                <div>
                  <Text size="xs" className="blue uppercase" style={{ letterSpacing: "0.04em" }}>
                    Rec. Frequency
                  </Text>
                  <Text className="font-medium mt-1">
                    {getRecFrequencyText(configs.obligationData.recFrequency)}
                  </Text>
                </div>
              )}
              <div>
                <Text size="xs" className="blue uppercase" style={{ letterSpacing: "0.04em" }}>
                  Act. Frequency
                </Text>
                <Text className="font-medium mt-1">
                  {getActFrequencyText(configs.obligationData.actFrequency)}
                </Text>
              </div>
              <div>
                <Text size="xs" className="blue uppercase" style={{ letterSpacing: "0.04em" }}>
                  Reminder Time
                </Text>
                <Text className="font-medium mt-1">{configs.obligationData.reminderTime}-days</Text>
              </div>
              <div>
                <Text size="xs" className="blue uppercase" style={{ letterSpacing: "0.04em" }}>
                  Due Date
                </Text>
                <Text className="font-medium mt-1">{formatDatetoISO(configs.obligationData.duedate)}</Text>
              </div>
            </div>
          </Paper>

          <Paper withBorder radius="md" p="md" className="mx-4 mt-4">
            <Text className="font-semibold mb-2" style={{ color: "#1b6f8d", letterSpacing: "0.03em" }}>
              AUDIT HISTORY
            </Text>
            <ActionHistory refresh={buttonConfigs.refresh} openFile={openFile} id={id} />
          </Paper>

          <Paper withBorder radius="md" p="md" className="mx-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="w-1/2">
                {canAct ? (
                  <Select
                    label="Compliance"
                    required
                    placeholder="Select compliance status"
                    error={complianceError}
                    value={selectedCompliance}
                    onChange={(val) => {
                      setSelectedCompliance(val);
                      setComplianceError("");
                    }}
                    data={
                      new Date() > new Date(configs.obligationData?.duedate)
                        ? [
                            { label: "Non-Compliant", value: "non-compliant" },
                            { label: "Partially-Compliant", value: "partially-compliant" },
                            { label: "Delayed Complaint", value: "delayed-compliant" },
                          ]
                        : [
                            { label: "Non-Compliant", value: "non-compliant" },
                            { label: "Partially-Compliant", value: "partially-compliant" },
                            { label: "Delayed Complaint", value: "delayed-compliant" },
                            { label: "Compliant", value: "compliant" },
                          ]
                    }
                  />
                ) : (
                  <div>
                    <Text className="font-normal blue">compliance : </Text>
                    <Text color={getComplianceBadgeColor(configs.obligationData.compliant)}>
                      {configs.obligationData.compliant}
                    </Text>
                  </div>
                )}
                {canReassign && (
                  <Select
                    className="mt-4"
                    label="Reassign"
                    placeholder="Unassigned"
                    clearable
                    value={
                      configs.obligationData.assignTo?.id
                        ? `${configs.obligationData.assignTo.id}`
                        : null
                    }
                    onChange={(val) => saveAssignee(val)}
                    data={assignableUsers.map((u) => ({ label: u.name, value: `${u.id}` }))}
                  />
                )}
              </div>
              <div>
                <Textarea
                  rows={2}
                  label="Remark"
                  placeholder="Remark"
                  value={configs.obligationData.remarks}
                  onChange={(e) =>
                    setConfigs({
                      ...configs,
                      obligationData: { ...configs.obligationData, remarks: e.target.value },
                    })
                  }
                />
              </div>
            </div>
          </Paper>

          <Paper withBorder radius="md" p="md" className="mx-4 mt-4">
            <Text className="font-semibold mb-2" style={{ color: "#1b6f8d", letterSpacing: "0.03em" }}>
              EVIDENCE
            </Text>
            {canAct && (
              <div className="flex items-center justify-center m-4">
                <label
                  htmlFor="dropzone-file"
                  className="flex flex-col items-center justify-center w-full h-34 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100"
                >
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <svg
                      className="w-8 h-8 mb-4 text-gray-500"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 20 16"
                    >
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                      />
                    </svg>
                    <p className="mb-2 text-sm text-gray-500">
                      <span>Click to upload</span> or drag and drop
                    </p>
                  </div>
                  <input
                    onChange={async (e) => {
                      await uploadFileToAws(e);
                    }}
                    id="dropzone-file"
                    type="file"
                    className="hidden"
                  />
                </label>
              </div>
            )}
            {uploadProgress && (
              <div className="mx-4 mb-4">
                <div className="flex justify-between mb-1">
                  <Text size="sm" color="dimmed">
                    Uploading {uploadProgress.fileName}...
                  </Text>
                  <Text size="sm" color="dimmed">
                    {uploadProgress.percent}%
                  </Text>
                </div>
                <Progress value={uploadProgress.percent} animate />
              </div>
            )}
            <div className="m-4">
              {files?.length > 0 &&
                files.map((evidenceFile) => (
                  <div
                    key={evidenceFile.id}
                    className="flex items-center mb-3 w-full p-3 rounded-lg bg-white"
                    style={{ border: "1px solid #e5e9eb" }}
                  >
                    <div className="flex items-center pl-1 w-full">
                      <Text
                        className="font-normal"
                        style={{ lineHeight: "1.25rem", letterSpacing: "-0.0313rem", color: "#191D23" }}
                      >
                        {evidenceFile.fileName}
                      </Text>
                    </div>
                    <div className="flex items-center justify-center">
                      <Button
                        className="mx-3 w-60"
                        variant="outline"
                        style={{ color: "#1b6f8d", borderColor: "#1b6f8d" }}
                        onClick={() => openFile({ file: evidenceFile })}
                      >
                        Open
                      </Button>
                    </div>
                    <div className="flex flex-col items-center justify-center">
                      {verification[evidenceFile.id]?.result ? (
                        <Badge
                          className="cursor-pointer"
                          size="lg"
                          color={verificationStatusColor(verification[evidenceFile.id].result.status)}
                          onClick={() => setResultModal({ opened: true, fileId: evidenceFile.id })}
                        >
                          {verification[evidenceFile.id].result.status}
                        </Badge>
                      ) : (
                        <Button
                          className="mx-3 w-60"
                          variant="outline"
                          color="teal"
                          loading={verification[evidenceFile.id]?.loading}
                          onClick={() => startEvidenceVerification(evidenceFile)}
                        >
                          Verify Evidence
                        </Button>
                      )}
                      {verification[evidenceFile.id]?.error && (
                        <Text className="mt-1 text-xs text-center" color="red">
                          {verification[evidenceFile.id].error}
                        </Text>
                      )}
                    </div>
                    {canAct && (
                      <div className="flex items-center justify-center w-1/12">
                        <IconTrash
                          onClick={(e) => deleteFileFromAws({ e, fileId: evidenceFile.id })}
                          className="cursor-pointer"
                          size={26}
                          color="#1b6f8d"
                        />
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </Paper>

          <div className="flex mt-6 justify-center items-center gap-4">
            {canAct && configs.obligationData.extraCol3 !== "1" && (
              <Button
                onClick={(e) => updateObligation(e)}
                style={{ width: "150px", backgroundColor: "#1b6f8d" }}
              >
                Save
              </Button>
            )}
            {canAct && configs.obligationData.extraCol3 === "1" && (
              <Button onClick={() => setShowModal(true)} style={{ width: "150px", backgroundColor: "#1b6f8d" }}>
                Submit Info
              </Button>
            )}
            {["Admin", "DeptAdmin", "CEO", "Auditor"].includes(userData.loginAs.name) &&
              configs.obligationData.extraCol3 === "" && (
                <Button
                  onClick={() => setShowModal(true)}
                  style={{ width: "150px", backgroundColor: "#1b6f8d" }}
                >
                  Need More Info
                </Button>
              )}
            {["Admin", "DeptAdmin", "CEO", "Auditor"].includes(userData.loginAs.name) &&
              configs.obligationData.extraCol3 === "2" && (
                <Button onClick={() => setShowModal(true)} variant="outline" style={{ width: "200px" }}>
                  Need More Information
                </Button>
              )}
            {["Admin", "DeptAdmin", "CEO", "Auditor"].includes(userData.loginAs.name) &&
              configs.obligationData.extraCol3 === "3" && (
                <Button variant="outline" style={{ width: "180px" }}>
                  Information Submitted
                </Button>
              )}
          </div>

          <Modal title="Need More Info" size="md" radius="md" onClose={() => setShowModal(false)} opened={showModal}>
            <Text size="sm" className="blue font-medium mb-1">
              Remarks
            </Text>
            <Textarea minRows={5} placeholder="Enter remark..." value={remarks} onChange={(e) => setRemarks(e.target.value)} />
            {canAct && configs.obligationData.extraCol3 === "1" && (
              <div className="flex mt-6 justify-end gap-3">
                <Button variant="outline" style={{ color: "#1b6f8d", borderColor: "#1b6f8d" }} onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button color="#1b6f8d" onClick={() => updateObligationnmi("2")}>
                  Submit
                </Button>
              </div>
            )}
            {["Admin", "DeptAdmin", "CEO", "Auditor"].includes(userData.loginAs.name) && (
              <div className="flex mt-6 justify-end gap-3">
                {configs.obligationData.extraCol3 === "2" && (
                  <>
                    <Button
                      variant="outline"
                      style={{ color: "#1b6f8d", borderColor: "#1b6f8d" }}
                      onClick={() => updateObligationnmi("1")}
                    >
                      Need More Info
                    </Button>
                    <Button color="#1b6f8d" onClick={() => updateObligationnmi("3")}>
                      Accept Info
                    </Button>
                  </>
                )}
                {configs.obligationData.extraCol3 === "" && (
                  <>
                    <Button variant="outline" style={{ color: "#1b6f8d", borderColor: "#1b6f8d" }} onClick={() => setShowModal(false)}>
                      Cancel
                    </Button>
                    <Button color="#1b6f8d" onClick={() => updateObligationnmi("1")}>
                      Need More Info
                    </Button>
                  </>
                )}
              </div>
            )}
          </Modal>
        </>
      )}
    </>
  );
};

export default ViewObligation;
